import { Alert, Button, Group, Modal, Stack, Text, Textarea } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useEffect, useState } from 'react';

interface Props {
  opened: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  isPending: boolean;
  confirmColor?: string;
  /**
   * Error message from the underlying mutation. When a non-empty string, an
   * alert is rendered inside the modal (which stays open) so the operator can
   * see why the action failed before retrying. Optional; existing callers that
   * don't pass it are unaffected.
   */
  error?: string | null;
  /**
   * Optional extra content rendered inside the modal, above the reason
   * textarea. Lets a caller add action-specific fields (e.g. a dermoscopy
   * exception reason on the AI-review trigger) without every action needing
   * its own modal. Optional — existing callers are unaffected.
   */
  children?: React.ReactNode;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}

/**
 * Shared reason-capture confirmation modal for every case-triage mutation
 * (case data correction, patient PHI correction, AI re-trigger, image
 * remove/add, status override). Every triage action requires a non-empty
 * `reason` string before the underlying API call fires — this component
 * is the single place that UX lives, so each action's own component just
 * supplies a title/description/confirm handler.
 */
export function ReasonConfirmModal({
  opened,
  title,
  description,
  confirmLabel,
  isPending,
  confirmColor,
  error,
  children,
  onConfirm,
  onClose,
}: Props) {
  const [reason, setReason] = useState('');

  // Reset the reason field whenever the modal reopens, regardless of how the
  // previous instance closed (cancel or successful confirm).
  useEffect(() => {
    if (!opened) return;
    setReason('');
  }, [opened]);

  function handleConfirm() {
    onConfirm(reason.trim());
  }

  const canConfirm = reason.trim().length > 0 && !isPending;

  return (
    <Modal opened={opened} onClose={onClose} title={title} centered>
      <Stack gap="md">
        <Text size="sm">{description}</Text>
        {children}
        <Textarea
          label="Reason (required, logged)"
          placeholder="Explain why this change is being made"
          required
          autosize
          minRows={2}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          disabled={isPending}
        />
        {error && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            {error}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            color={confirmColor}
            loading={isPending}
            disabled={!canConfirm}
            onClick={handleConfirm}
          >
            {confirmLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
